/**
 * Comparaison : une métrique sur deux périodes côte à côte + la variation.
 *
 * Pas d'ECharts — DOM + Tailwind. La logique de calcul est isolée dans
 * `computeComparison` (fonction pure, testée sans DOM).
 */
import type { WidgetConfig } from '../schemas';
import { useFieldCatalog, metricLabel, metricFormat, metricGoodDirection, type FieldCatalog } from '../fieldCatalog';
import { formatMetricValue, type MetricFormat } from '../formatValue';
import { aggregateMetric } from '../aggregate';
import { TrendIndicator } from '../../../components/ui/TrendIndicator';
import { CHART_COLORS, COLOR_PREVIOUS, COLOR_PRIMARY } from '../chartStyle';
import { computeCompare, type CompareParts } from '../compareFormat';

type Row = Record<string, string | number>;

interface WidgetData {
  current: Row[];
  previous: Row[] | null;
  meta: any;
}

interface WidgetProps {
  config: WidgetConfig;
  data: WidgetData | null;
  loading?: boolean;
  error?: string;
}

// Cadre fourni par `WidgetShell` (WidgetRenderer) : ici, padding + hauteur seulement.
const SHELL = 'h-full';

// Tables de correspondance vers des classes Tailwind LITTÉRALES : jamais de
// `text-${x}` par template, sinon la purge Tailwind les supprime du build.
const FONT: Record<string, string> = { sans: 'font-sans', serif: 'font-serif', mono: 'font-mono' };
const ALIGN: Record<string, string> = { left: 'text-left', center: 'text-center', right: 'text-right' };
const TITLE_SIZE: Record<string, string> = { xs: 'text-xs', sm: 'text-sm', base: 'text-base' };
const VALUE_SIZE: Record<string, string> = {
  xl: 'text-xl',
  '2xl': 'text-2xl',
  '3xl': 'text-3xl',
  '4xl': 'text-4xl',
  '5xl': 'text-5xl',
};

const DEFAULT_CURRENT_LABEL = 'Période actuelle';
const DEFAULT_PREVIOUS_LABEL = 'Période précédente';

export interface ComparisonResult {
  /** Libellé FR de la métrique (jamais la clé brute). */
  label: string;
  /** Valeur agrégée de la période courante (ratio-safe). */
  current: number;
  /** Valeur agrégée de la période précédente, `null` si le backend ne l'a pas renvoyée. */
  previous: number | null;
  currentLabel: string;
  previousLabel: string;
  currentPeriodLabel: string;
  previousPeriodLabel: string;
  /** Variation en %, arrondie à une décimale. `null` si pas de base de comparaison. */
  variation: number | null;
  /** Primitives de comparaison selon le format par métrique (percent / diff / absolute). */
  compare: CompareParts;
  /** Format d'affichage de la métrique (pour formater le delta en mode 'diff'). */
  format: MetricFormat;
  /** Sens favorable de la métrique (override widget > défaut catalogue > 'up'). */
  goodDirection: 'up' | 'down';
  /** `false` → état « À configurer ». */
  configured: boolean;
}

function periodLabel(config: WidgetConfig, key: 'current_label' | 'previous_label', fallback: string): string {
  const raw = config.display?.[key];
  return typeof raw === 'string' && raw.trim() ? raw : fallback;
}

/**
 * Réduit la réponse de requête aux deux périodes comparées.
 *
 * La variation s'affiche **dès que `data.previous` existe** : la comparaison peut venir
 * du rapport (période) et pas seulement du widget — exiger `config.compare` la masquerait
 * (bug déjà corrigé sur `MetricCardWidget`). Une base précédente à 0 (ou négative) ne
 * donne pas de variation : pas de division par zéro.
 */
export function computeComparison(
  config: WidgetConfig,
  data: WidgetData | null,
  catalog: FieldCatalog | null,
): ComparisonResult {
  const metric = config.metrics?.[0];
  const format = metric ? metricFormat(catalog, metric) : 'number';

  const base = {
    label: metric ? metricLabel(catalog, metric, config.metric_labels) : '',
    currentPeriodLabel: periodLabel(config, 'current_label', DEFAULT_CURRENT_LABEL),
    previousPeriodLabel: periodLabel(config, 'previous_label', DEFAULT_PREVIOUS_LABEL),
    goodDirection: metric
      ? metricGoodDirection(catalog, metric, config.metric_good_direction?.[metric])
      : ('up' as const),
  };

  if (!metric || !data) {
    return {
      ...base,
      current: 0,
      previous: null,
      currentLabel: '—',
      previousLabel: '—',
      variation: null,
      compare: computeCompare(0, null, 'percent'),
      format,
      configured: false,
    };
  }

  const def = catalog?.metrics.find((m) => m.key === metric);
  const current = aggregateMetric(data.current, metric, def);
  const previous = data.previous ? aggregateMetric(data.previous, metric, def) : null;

  const compareFormat = config.metric_compare_format?.[metric] ?? 'percent';
  const compare = computeCompare(current, previous, compareFormat);

  return {
    ...base,
    current,
    previous,
    currentLabel: formatMetricValue(current, format),
    previousLabel: previous === null ? '—' : formatMetricValue(previous, format),
    variation: compare.percent,
    compare,
    format,
    configured: true,
  };
}

export default function ComparisonMetricWidget({ config, data, loading, error }: WidgetProps) {
  // Hook avant tout early return (règle des hooks).
  const catalog = useFieldCatalog(data?.meta?.source_type as string | undefined);

  if (loading) {
    return (
      <div className={SHELL}>
        <div className="h-3 w-1/2 bg-[#F4F5FA] rounded animate-pulse mb-4" />
        <div className="grid grid-cols-2 gap-4">
          <div className="h-7 bg-[#F4F5FA] rounded animate-pulse" />
          <div className="h-7 bg-[#F4F5FA] rounded animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${SHELL} flex items-center`}>
        <span className="text-sm text-red-600">{error}</span>
      </div>
    );
  }

  const comparison = computeComparison(config, data, catalog);

  if (!config.data_source_id || !comparison.configured) {
    return (
      <div className={`${SHELL} flex items-center justify-center`}>
        <span className="text-sm text-[#8B8FA7]">À configurer</span>
      </div>
    );
  }

  // Style (StyleTab). Valeurs par défaut = rendu historique frameless.
  const d = config.display ?? {};
  const fontClass = FONT[d.font_family ?? 'sans'] ?? FONT.sans;
  const alignTitle = ALIGN[d.align_title ?? 'left'] ?? ALIGN.left;
  const alignValue = ALIGN[d.align_value ?? 'left'] ?? ALIGN.left;
  const alignComparison = ALIGN[d.align_comparison ?? 'left'] ?? ALIGN.left;
  const titleSize = TITLE_SIZE[d.title_size ?? 'sm'] ?? TITLE_SIZE.sm;
  const valueSize = VALUE_SIZE[d.value_size ?? '2xl'] ?? VALUE_SIZE['2xl'];
  const titleColor = d.title_color ?? '#8B8FA7';

  const titleClass = [
    titleSize,
    'font-medium',
    alignTitle,
    d.title_italic ? 'italic' : '',
    d.title_underline ? 'underline' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={`${SHELL} flex flex-col justify-center ${fontClass}`}>
      <div className={`${titleClass} mb-3`} style={{ color: titleColor }}>
        {config.title || comparison.label}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className={alignValue}>
          <div className="text-xs text-[#8B8FA7] mb-1 truncate">{comparison.currentPeriodLabel}</div>
          <p
            className={`${valueSize} font-bold font-mono leading-none`}
            style={{ color: COLOR_PRIMARY }}
          >
            {comparison.currentLabel}
          </p>
        </div>
        <div className={alignValue}>
          <div className="text-xs text-[#8B8FA7] mb-1 truncate">{comparison.previousPeriodLabel}</div>
          <p
            className={`${valueSize} font-bold font-mono leading-none`}
            style={{ color: comparison.previous === null ? COLOR_PREVIOUS : CHART_COLORS[2] }}
          >
            {comparison.previousLabel}
          </p>
        </div>
      </div>

      {comparison.compare.showDelta &&
        comparison.compare.kind === 'percent' &&
        comparison.compare.percent !== null && (
          <div className={`mt-3 ${alignComparison}`}>
            <TrendIndicator value={comparison.compare.percent} goodDirection={comparison.goodDirection} />
          </div>
        )}
      {comparison.compare.showDelta &&
        comparison.compare.kind === 'diff' &&
        comparison.compare.diff !== null && (
          <div className={`mt-3 ${alignComparison}`}>
            <TrendIndicator
              value={comparison.compare.diff}
              formattedValue={`${comparison.compare.diff > 0 ? '+' : ''}${formatMetricValue(
                comparison.compare.diff,
                comparison.format,
              )}`}
              goodDirection={comparison.goodDirection}
            />
          </div>
        )}
      {comparison.compare.showDelta &&
        comparison.compare.kind === 'both' &&
        comparison.compare.diff !== null && (
          <div className={`mt-3 ${alignComparison}`}>
            <TrendIndicator
              value={comparison.compare.percent ?? comparison.compare.diff}
              formattedValue={`${comparison.compare.diff > 0 ? '+' : ''}${formatMetricValue(
                comparison.compare.diff,
                comparison.format,
              )}${
                comparison.compare.percent !== null
                  ? ` (${comparison.compare.percent > 0 ? '+' : ''}${comparison.compare.percent}%)`
                  : ''
              }`}
              goodDirection={comparison.goodDirection}
            />
          </div>
        )}
    </div>
  );
}
